import { useMutation, useQueryClient } from 'react-query'

import { type RepoError } from '@webapp/infrastructure/interfaces/repositories/RepoError'
import {
  type TermsMethodsKindEnum,
  TermsMethodsKinds,
} from '@webapp/modules/purchasing/entities/enums/TermsMethodsKindEnum'
// eslint-disable-next-line import/no-internal-modules
import { paymentTermRepository } from '@webapp/modules/vendor/data-access/repositories/PaymentTermRepository'
// eslint-disable-next-line import/no-internal-modules
import { shippingMethodRepository } from '@webapp/modules/vendor/data-access/repositories/ShippingMethodRepository'
// eslint-disable-next-line import/no-internal-modules
import { shippingTermRepository } from '@webapp/modules/vendor/data-access/repositories/ShippingTermRepository'
// eslint-disable-next-line import/no-internal-modules
import { type PaymentTerm } from '@webapp/modules/vendor/entities/PaymentTerm'
// eslint-disable-next-line import/no-internal-modules
import { type ShippingMethod } from '@webapp/modules/vendor/entities/ShippingMethod'
// eslint-disable-next-line import/no-internal-modules
import { type ShippingTerm } from '@webapp/modules/vendor/entities/ShippingTerm'
import {
  paymentTermQueryConfig,
  shippingMethodQueryConfig,
  shippingTermQueryConfig,
} from '@webapp/modules/vendor/query-configs' // eslint-disable-line import/no-internal-modules

export type TermOrMethod = ShippingTerm | PaymentTerm | ShippingMethod

export type TermOrMethodEditableField = 'name' | 'description'

interface EditTermOrMethodMutationVariables {
  item: TermOrMethod
  field: TermOrMethodEditableField
  value: string
}

// Kind-switch lives in the Model layer, not the Feature Set
const KIND_REPOSITORIES = {
  [TermsMethodsKinds.SHIPPING_TERMS]: shippingTermRepository,
  [TermsMethodsKinds.PAYMENT_TERMS]: paymentTermRepository,
  [TermsMethodsKinds.SHIPPING_METHODS]: shippingMethodRepository,
} as const

const KIND_QUERY_CONFIGS = {
  [TermsMethodsKinds.SHIPPING_TERMS]: shippingTermQueryConfig,
  [TermsMethodsKinds.PAYMENT_TERMS]: paymentTermQueryConfig,
  [TermsMethodsKinds.SHIPPING_METHODS]: shippingMethodQueryConfig,
} as const

export const useMutateEditTermOrMethod = (kind: TermsMethodsKindEnum) => {
  const queryClient = useQueryClient()

  const mutation = useMutation<
    void,
    RepoError,
    EditTermOrMethodMutationVariables
  >({
    // PUT the whole row with the edited field replaced (BHV-030)
    mutationFn: ({ item, field, value }) =>
      KIND_REPOSITORIES[kind].update(item.id, {
        name: field === 'name' ? value : item.name,
        description: field === 'description' ? value : item.description,
        active: item.active,
      }),
    onSuccess: () => {
      // Per-kind isolation — invalidate only the active kind's aggregate
      queryClient.invalidateQueries({
        queryKey: KIND_QUERY_CONFIGS[kind].all().queryKey,
      })
    },
  })

  return {
    mutateAsync: mutation.mutateAsync,
    isLoading: mutation.isLoading,
  }
}
